import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/about-us", label: "About Us", testid: "nav-link-about-us" },
  { to: "/products", label: "Our Products", testid: "nav-link-products" },
  { to: "/about-homeopathy", label: "About Homeopathy", testid: "nav-link-about-homeopathy" },
  { to: "/contact", label: "Contact Us", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-border" data-testid="main-navbar">
      <nav className="container-xl flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/ehp-logo.jpg" alt="EHP logo" className="h-12 w-12 rounded-full object-contain bg-white ring-1 ring-border" />
          <div className="leading-tight">
            <div className="font-serif text-xl font-700 text-[var(--brand-navy)]">EHP</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-primary bg-secondary" : "text-foreground/70 hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {isAdmin && (
            <Link to="/admin" className="btn-outline !px-4 !py-2" data-testid="nav-admin-button">
              <LayoutDashboard className="h-4 w-4" /> Admin
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="btn-primary !px-4 !py-2" data-testid="nav-logout-button">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          ) : (
            <>
              <Link to="/login" className="btn-outline !px-4 !py-2" data-testid="nav-login-button">
                <UserIcon className="h-4 w-4" /> Login
              </Link>
              <Link to="/register" className="btn-primary !px-4 !py-2" data-testid="nav-register-button">
                Register
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 pb-6 pt-2" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`m-${l.testid}`}
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-sm font-medium ${isActive ? "bg-secondary text-primary" : "text-foreground/80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-3 flex flex-col gap-2">
            {isAdmin && (
              <Link to="/admin" onClick={() => setOpen(false)} className="btn-outline" data-testid="m-nav-admin-button">
                Admin Dashboard
              </Link>
            )}
            {user ? (
              <button onClick={() => { setOpen(false); handleLogout(); }} className="btn-primary" data-testid="m-nav-logout-button">
                Logout
              </button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline" data-testid="m-nav-login-button">Login</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary" data-testid="m-nav-register-button">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
